export interface NewsArticle {
  id: number;
  category: string;
  date: string;
  title: string;
  excerpt: string;
  image: string;
  author?: string;
  authorTitle?: string;
  readTime?: string;
  body?: string[];
  tags?: string[];
  status?: 'published' | 'draft';
  createdAt?: string;
  updatedAt?: string;
}

export interface Registration {
  id: number;
  name: string;
  email: string;
  phone: string;
  company: string;
  products: string[];
  message: string;
  admin_note: string;
  status: 'new' | 'contacted' | 'closed';
  created_at: string;
}

export interface Inquiry {
  id: number;
  name: string;
  company: string;
  email: string;
  phone: string;
  cooperation_type: string;
  message: string;
  admin_note: string;
  status: 'new' | 'contacted' | 'closed';
  created_at: string;
}

export interface DownloadSettings {
  iosAppStoreUrl: string;
  androidApkUrl: string;
  androidApkPath: string;
  androidApkName: string;
  androidApkSize: number;
  wechatQrUrl: string;
  wechatQrPath: string;
  updatedAt: string;
}

export interface UploadedFile {
  url: string;
  path: string;
  mimeType: string;
  size: number;
  name?: string;
}

async function request<T>(url: string, options: any = {}): Promise<T> {
  const isJsonBody = typeof options.body === 'string';
  const response = await fetch(url, {
    credentials: 'same-origin',
    ...options,
    headers: {
      ...(isJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || data.error || '请求失败');
  }
  return data;
}

export function getPublicNews() {
  return request<{ items: NewsArticle[] }>('/api/news');
}

export function getPublicNewsDetail(id: number) {
  return request<{ item: NewsArticle }>(`/api/news/${id}`);
}

export function getDownloadSettings() {
  return request<{ item: DownloadSettings }>('/api/download-settings');
}

export function submitRegistration(payload: Record<string, unknown>) {
  return request<{ ok: true; id: number }>('/api/registrations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function submitInquiry(payload: Record<string, unknown>) {
  return request<{ ok: true; id: number }>('/api/inquiries', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export const adminApi = {
  login(username: string, password: string) {
    return request<{ ok: true; username: string }>('/api/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },
  logout() {
    return request<{ ok: true }>('/api/admin/logout', { method: 'POST' });
  },
  me() {
    return request<{ username: string }>('/api/admin/me');
  },
  setupStatus() {
    return request<{ needsSetup: boolean }>('/api/admin/setup-status');
  },
  setup(username: string, password: string) {
    return request<{ ok: true; username: string }>('/api/admin/setup', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },
  news() {
    return request<{ items: NewsArticle[] }>('/api/admin/news');
  },
  createNews(article: Partial<NewsArticle>) {
    return request<{ item: NewsArticle }>('/api/admin/news', {
      method: 'POST',
      body: JSON.stringify(article),
    });
  },
  updateNews(id: number, article: Partial<NewsArticle>) {
    return request<{ item: NewsArticle }>(`/api/admin/news/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(article),
    });
  },
  uploadImage(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return request<{ item: UploadedFile }>('/api/admin/uploads', {
      method: 'POST',
      body: formData,
    });
  },
  uploadApk(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return request<{ item: UploadedFile }>('/api/admin/uploads/apk', {
      method: 'POST',
      body: formData,
    });
  },
  downloadSettings() {
    return request<{ item: DownloadSettings }>('/api/admin/download-settings');
  },
  updateDownloadSettings(settings: Partial<DownloadSettings>) {
    return request<{ item: DownloadSettings }>('/api/admin/download-settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  },
  deleteNews(id: number) {
    return request<{ ok: true }>(`/api/admin/news/${id}`, { method: 'DELETE' });
  },
  registrations() {
    return request<{ items: Registration[] }>('/api/admin/registrations');
  },
  updateRegistration(id: number, status: Registration['status']) {
    return request<{ ok: true }>(`/api/admin/registrations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },
  updateRegistrationNote(id: number, adminNote: string) {
    return request<{ ok: true }>(`/api/admin/registrations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ adminNote }),
    });
  },
  inquiries() {
    return request<{ items: Inquiry[] }>('/api/admin/inquiries');
  },
  updateInquiry(id: number, status: Inquiry['status']) {
    return request<{ ok: true }>(`/api/admin/inquiries/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },
  updateInquiryNote(id: number, adminNote: string) {
    return request<{ ok: true }>(`/api/admin/inquiries/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ adminNote }),
    });
  },
};
